// The Maker: the one page for anything made with AI (a picture, a voice take, an animation). Top to bottom: the inputs
// for the medium, the "Start from" strip of versions, the words and the Make row, then the picked version big, with
// anything medium-specific under it. These are the shared pieces; each medium (ImageMaker, VoiceMaker, MotionStage)
// puts its own inputs and words between them. See docs/ui-patterns.md, pattern A.
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { Body, Footer, Icon, ModelSelect, Page, Toast, TopBar } from "../common";
import "./maker.css";

// The page itself: top bar, the sections as its children, an optional footer (flow navigation only, never Make) and
// a toast. The caller wraps it in a Layer, as it may have pickers to open over it.
export function MakerPage({ title, sub, onBack, footer, toast, clearToast, className, page, children }: {
  title: ReactNode; sub?: ReactNode; onBack?: () => void; footer?: ReactNode; toast?: string | null; clearToast?: () => void; className?: string; page?: string; children: ReactNode;
}) {
  return (
    <Page className={`tl-page mk${className ? ` ${className}` : ""}`}>
      <TopBar onBack={onBack} title={title} sub={sub} />
      <Body page={page}>{children}</Body>
      {footer && <Footer>{footer}</Footer>}
      {toast && <Toast onClick={clearToast}>{toast}</Toast>}
    </Page>
  );
}

// A small sound wave, so a take reads as audio.
export function WaveIcon() {
  return (
    <svg viewBox="0 0 48 24" width="48" height="24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
      {[6, 14, 20, 10, 18, 22, 12, 16, 8].map((h, i) => <line key={i} x1={4 + i * 5} x2={4 + i * 5} y1={12 - h / 2} y2={12 + h / 2} />)}
    </svg>
  );
}

// One version in the strip: a picture (`src`) or a sound wave (`wave`), its label ("V3") and a short line under it.
export type StripVersion = { id: string; label: string; sub?: ReactNode; src?: string; wave?: boolean; on: boolean; title?: string };

// The "Start from" strip. First the fresh tile (Blank, or Still for animations), then a tile for a version being made
// (a spinner), or a red one for a version that failed (tap it to try again), then every version, newest first.
// Tapping a tile picks it; the caller decides what that does.
export function VersionStrip({ title = "Start from", hint, note, fresh, making, failed, versions, onPick, disabled }: {
  title?: string; hint?: ReactNode; note?: boolean; // note: "Each change is kept as a new version." under pictures
  fresh: { label: string; sub?: ReactNode; src?: string; on: boolean; onPick: () => void }; // src: the picture it stands for (Still); none: a plus (Blank)
  making?: { label: string; word: string }; // "V4", "Drawing"
  failed?: { label: string; onRetry: () => void };
  versions: StripVersion[]; onPick: (id: string) => void;
  disabled?: boolean; // taps are off while something is being made
}) {
  const sound = versions.some(v => v.wave); // sound versions are small white tiles with the length under them; pictures are tall thumbnails
  const kind = sound ? "mk-s" : "mk-v";
  return (
    <section className="section">
      <div className="section-head"><h2>{title}</h2></div>
      {hint && <p className="hint">{hint}</p>}
      <div className={`mk-strip ${kind}s`} role="radiogroup" aria-label={title}>
        <button role="radio" aria-checked={fresh.on} className={`${kind} fresh${fresh.on ? " on" : ""}`} disabled={disabled} onClick={fresh.onPick}>
          {fresh.src ? <img src={fresh.src} alt="" /> : sound ? null : <Icon name="plus" size={20} />}
          <span className={`${kind}-label`}>{fresh.label}</span>
          {fresh.sub && <span className={`${kind}-sub`}>{fresh.sub}</span>}
        </button>
        {making && (
          <div className={`${kind} making`} role="status">
            <span className="spinner" />
            <span className={`${kind}-label`}>{sound ? `${making.label} · ${making.word}…` : making.label}</span>
          </div>
        )}
        {failed && (
          <button className={`${kind} failed`} onClick={failed.onRetry} disabled={disabled}>
            <Icon name="replay" size={18} />
            <span className={`${kind}-label`}>{sound ? `${failed.label} · Try again` : failed.label}</span>
          </button>
        )}
        {versions.map(v => (
          <button key={v.id} role="radio" aria-checked={v.on} aria-label={`${v.label}${v.on ? ", in use" : ""}`} className={`${kind}${v.on ? " on" : ""}`} disabled={disabled} title={v.title} onClick={() => onPick(v.id)}>
            {v.src && <img src={v.src} alt="" />}
            <span className={`${kind}-label`}>{v.label}{sound && v.on ? " · in use" : ""}</span>
            {v.sub && <span className={`${kind}-sub`}>{v.sub}</span>}
          </button>
        ))}
        {!sound && note && <span className="mk-note">Each change is kept as a new version.</span>}
      </div>
    </section>
  );
}

// The picked version, big. With nothing picked (the fresh tile) it is an empty dashed frame saying where the next one
// will appear. While one is being made, one overlay with a line and a bar sits over it. A bin in the top right corner
// deletes the picked version (`onDelete`; the caller says if that is immediate or asks first).
// `tall`: a picture, as high as the screen allows and as wide as its shape says. Otherwise as wide as the page.
export function ResultFrame({ aspect, tall, empty, busy, onDelete, deleteLabel = "Delete this version", className, style, children }: {
  aspect?: number; tall?: boolean;
  empty?: string; // nothing picked: "V4 appears here"
  busy?: { message: string; since?: number; ms?: number }; // "Drawing V4…"
  onDelete?: () => void; deleteLabel?: string;
  className?: string; style?: CSSProperties; children?: ReactNode;
}) {
  const shape: CSSProperties = { ...(aspect ? { aspectRatio: aspect } : {}), ...style };
  return (
    <div className="mk-top">
      <div className={`mk-result${tall ? " tall" : ""}${empty !== undefined ? " empty" : ""}${className ? ` ${className}` : ""}`} style={shape}>
        {empty !== undefined ? <span className="mk-empty">{empty}</span> : children}
        {busy && <MakerProgress {...busy} />}
        {onDelete && empty === undefined && !busy && (
          <button className="mk-bin" aria-label={deleteLabel} title={deleteLabel} onClick={onDelete}><Icon name="trash" size={18} /></button>
        )}
      </div>
    </div>
  );
}

// Over the result while it is made: one line ("Drawing V4… You can move on. It keeps going.") and a bar that fills
// smoothly towards 90% across `ms`, then waits for the result.
function MakerProgress({ message, since, ms = 20_000 }: { message: string; since?: number; ms?: number }) {
  const [began] = useState(() => since ?? Date.now()); // when it began here, if the caller doesn't know
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 200); return () => clearInterval(t); }, []);
  const x = Math.min(1, Math.max(0, now - (since ?? began)) / ms);
  const pct = Math.round(90 * (1 - (1 - x) ** 2));
  return (
    <div className="mk-busy" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={message}>
      <p><b>{message}</b> You can move on. It keeps going.</p>
      <div className="mk-track"><div className="mk-fill" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

// The Make row: the model dropdowns side by side (only where the medium has a choice), the Make button full width under them.
// Yellow for making, red for recording, plain white (`plain`) when something else on the page is the next step.
export function MakeRow({ model, red, plain, disabled, onClick, children }: { model?: boolean; red?: boolean; plain?: boolean; disabled?: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <div className={`mk-make${model ? " with-model" : ""}`}>
      {model && <ModelSelect bare />}
      <button className={`btn big wide${red ? " red" : plain ? " plain" : " yellow"}`} disabled={disabled} onClick={onClick}>{children}</button>
    </div>
  );
}
